/**
 * Constant for storing tenant ID in local storage
 */
export const TENANT_ID_STORAGE_KEY = 'tenantId';

// Import caching utilities 
import tenantCache from './tenantCache';

// Cache timeout in milliseconds (5 seconds)
const CACHE_TIMEOUT = 5000;

/**
 * Returns a valid tenant ID from various possible sources
 * @param {Object} options - Sources for tenant ID
 * @param {Object} [options.tenant] - Tenant object from context
 * @param {boolean} [options.useLocalStorage=true] - Whether to check localStorage
 * @param {boolean} [options.forceRefresh=false] - Force fetching a new ID even if cached
 * @param {string} [options.defaultId=null] - Default ID to use if no other sources available
 * @returns {string|null} The tenant ID or null if not found
 */
export const getValidTenantId = (options = {}) => {
  const { tenant, useLocalStorage = true, forceRefresh = false, defaultId = null } = options;
  
  // Check memory cache first if not forcing refresh (fastest path)
  if (!forceRefresh) {
    const cachedId = tenantCache.getCachedTenantId();
    if (cachedId) {
      return cachedId;
    }
  }
  
  // Try to get from tenant object first (most reliable source)
  if (tenant && tenant._id) {
    tenantCache.cacheTenantId(tenant._id);
    return tenant._id;
  }
  
  // Then check localStorage if allowed
  if (useLocalStorage) {
    // First try direct tenantId key
    const storedId = localStorage.getItem(TENANT_ID_STORAGE_KEY);
    if (storedId) {
      tenantCache.cacheTenantId(storedId);
      return storedId;
    }
    
    // Then try from currentTenant object
    const storedTenant = localStorage.getItem('currentTenant');
    if (storedTenant) {
      try {
        const tenantData = JSON.parse(storedTenant);
        if (tenantData && (tenantData._id || tenantData.id)) {
          const tenantId = tenantData._id || tenantData.id;
          // Update the primary storage key for consistency
          localStorage.setItem(TENANT_ID_STORAGE_KEY, tenantId);
          tenantCache.cacheTenantId(tenantId);
          return tenantId;
        }
      } catch (e) {
        console.error('Error parsing stored tenant:', e);
      }
    }
    
    // Finally try from user object
    const storedUser = localStorage.getItem('authUser');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData && userData.tenantId) {
          // Update the primary storage key for consistency
          localStorage.setItem(TENANT_ID_STORAGE_KEY, userData.tenantId);
          tenantCache.cacheTenantId(userData.tenantId);
          return userData.tenantId;
        }
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
  }
  
  // Fall back to default or environment variable
  if (defaultId) {
    tenantCache.cacheTenantId(defaultId);
    return defaultId;
  }
  
  // Check environment variable as last resort
  if (process.env.NODE_ENV === 'development' && process.env.VITE_DEFAULT_TENANT_ID) {
    const envTenantId = process.env.VITE_DEFAULT_TENANT_ID;
    tenantCache.cacheTenantId(envTenantId);
    return envTenantId;
  }
  
  return null;
};

/**
 * Ensures a tenant ID is set in localStorage
 * @param {Object|string} tenant - The tenant object or tenant ID
 * @returns {string|null} The tenant ID that was set, or null if invalid
 */
export const ensureTenantId = (tenant) => {
  let tenantId = null;
  
  // Handle if tenant is an object
  if (typeof tenant === 'object' && tenant !== null) {
    tenantId = tenant._id || tenant.id;
  }
  // Handle if tenant is a direct ID string
  else if (typeof tenant === 'string') {
    tenantId = tenant;
  }
  
  if (!tenantId) return null;
  
  // Store using consistent key - only update if changed
  const currentId = localStorage.getItem(TENANT_ID_STORAGE_KEY);
  if (currentId !== tenantId) {
    localStorage.setItem(TENANT_ID_STORAGE_KEY, tenantId);
  }
  
  // Update cache
  tenantCache.cacheTenantId(tenantId);
  
  return tenantId;
};

/**
 * Validates if a string is a valid MongoDB ObjectId
 * @param {string} id - The ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidObjectId = (id) => {
  if (!id) return false;
  // Simple MongoDB ObjectId validation regex
  const objectIdPattern = /^[0-9a-fA-F]{24}$/;
  return objectIdPattern.test(id);
};

/**
 * Safely access a nested property in an object with a path
 * @param {Object} obj - The object to access
 * @param {string} path - The dot-notation path to the property
 * @param {*} defaultValue - Default value if property doesn't exist
 * @returns {*} The property value or default value
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || !path) return defaultValue;
  
  const keys = path.split('.');
  let current = obj;
  
  for (const key of keys) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return defaultValue;
    }
    current = current[key];
  }
  
  return current !== undefined ? current : defaultValue;
};

/**
 * Extracts a tenant ID from various sources in a component
 * @param {Object} props - Component props
 * @param {Object} context - Context containing tenant info
 * @returns {string|null} The tenant ID or null if not found
 */
export const extractTenantId = (props, context) => {
  // Check cached ID first for performance
  const cachedId = tenantCache.getCachedTenantId();
  if (cachedId) {
    return cachedId;
  }
  
  // Check URL params first
  if (props?.match?.params?.tenantId) {
    const id = props.match.params.tenantId;
    tenantCache.cacheTenantId(id);
    return id;
  }
  
  // Then check props
  if (props?.tenantId) {
    tenantCache.cacheTenantId(props.tenantId);
    return props.tenantId;
  }
  
  // Then check context
  if (context?.tenant?._id) {
    tenantCache.cacheTenantId(context.tenant._id);
    return context.tenant._id;
  }
  
  // Finally check localStorage
  const storedId = localStorage.getItem(TENANT_ID_STORAGE_KEY);
  if (storedId) {
    tenantCache.cacheTenantId(storedId);
  }
  return storedId;
};

/**
 * Get mock tenant data for development or when API fails
 * @returns {Object} Mock tenant data
 */
export const getMockTenant = () => {
  return {
    _id: '67e9b1794d4dd4221be503b6',
    id: '67e9b1794d4dd4221be503b6',
    name: 'Demo Organization',
    tenantName: 'Demo Organization',
    email: 'admin@example.com',
    settings: {
      membershipFields: [
        { name: 'address', label: 'Address', required: false },
        { name: 'phone', label: 'Phone Number', required: false }
      ],
      organization: {
        logo: null,
        primaryColor: '#3f51b5',
        secondaryColor: '#f50057'
      }
    }
  };
};

/**
 * Fallback to mock tenant if no real tenant is available
 * Only used in development environments
 * @returns {string} Tenant ID
 */
export const getDevFallbackTenantId = () => {
  if (process.env.NODE_ENV !== 'production') {
    const mockTenant = getMockTenant();
    ensureTenantId(mockTenant);
    return mockTenant._id;
  }
  return null;
};

export default {
  getValidTenantId,
  ensureTenantId,
  isValidObjectId,
  getNestedValue,
  extractTenantId,
  getMockTenant,
  getDevFallbackTenantId
};
